import React, { useEffect, useState } from 'react'

import { FormControlLabel, Grid, Switch, TextField } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Controller, useFormContext } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'

import { AutocompleteTypeAndSearch, Card, ConfirmationDialog } from 'components'

import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'
import useSnackbar from 'hooks/useSnackbar'

import { routes } from 'Routes'
import helpers from 'helpers'
import * as service from 'service'
import constants from 'constants/index'

const GeneralInfoStep = () => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const { control, errors, register, watch, handleSubmit, triggerValidation } =
    useFormContext()
  const {
    handleNext,
    setIsLoading,
    setCompanySupplierId,
    resetAdvanceStep,
    wantsToAdvanceStep,
    isEdit,
    isShow,
    companySupplier,
    refresh,
    isPreAvaliation,
  } = useThirdPartyManagementStepper()
  const snackbar = useSnackbar()
  const history = useHistory()

  const international = watch('international')
  const document = watch('document')
  const simplifiedRegister = watch('simplifiedRegister')

  const showSimplifiedRegisterSwitch = !isEdit && !isShow

  if (!showSimplifiedRegisterSwitch) register({ name: 'simplifiedRegister' })

  const supplier = companySupplier?.supplier || {}

  const stripDocument = (value) =>
    international
      ? (value || '').trim()
      : helpers.regex.onlyNumbersAndLetters(value)

  const getGeneralInfoChanges = () => ({
    internationalChanged: !!international !== !!supplier?.international,
    documentChanged:
      stripDocument(document) !== stripDocument(supplier?.document),
  })

  const hasCriticalChange = () => {
    const { internationalChanged, documentChanged } = getGeneralInfoChanges()

    return internationalChanged || documentChanged
  }

  const isInternationalOnlyChange = () => {
    const { internationalChanged, documentChanged } = getGeneralInfoChanges()

    return internationalChanged && !documentChanged
  }

  const fetchSegments = async (inputValue) => {
    try {
      const response = await service.dponet.segments.get({
        name: inputValue,
      })

      return response.data.segments
    } catch {
      return []
    }
  }

  const { STEPS } = constants.tours.thirdPartyManagements

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)

      data.segmentId = data.segmentId?.id
      data.document = data.document?.trim()

      if (isEdit) {
        await service.dponet.suppliers.generalInfo.update({
          ...data,
          ...(isPreAvaliation && { preAvaliation: true }),
          companySupplierId: companySupplier?.id,
        })

        refresh()

        snackbar.open({
          message: 'Etapa de Informações Gerais atualizada com sucesso',
          variant: 'success',
        })
      } else {
        const response = await service.dponet.suppliers.generalInfo.create({
          ...data,
          ...(isPreAvaliation && { preAvaliation: true }),
        })

        const companySupplierId = response?.data?.companySupplier?.id

        setCompanySupplierId(companySupplierId)

        const redirectSearch = new URLSearchParams({ fromCreate: 'true' })
        if (isPreAvaliation) redirectSearch.set('preAvaliation', 'true')

        history.push(
          `${reverse(routes.thirdPartyManagements.mySuppliers.edit, {
            companySupplierId,
          })}?${redirectSearch.toString()}`,
        )
      }

      resetAdvanceStep()
      handleNext()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao criar fornecedor',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
      resetAdvanceStep()
    }
  }

  const triggerSubmit = () => {
    let validationPassed = false

    // Para não travar o formulário se der erro de validação do yup
    handleSubmit((data) => {
      validationPassed = true
      return onSubmit(data)
    })().then(() => {
      if (!validationPassed) resetAdvanceStep()
    })
  }

  const handleConfirmCriticalChange = () => {
    setConfirmDialogOpen(false)
    triggerSubmit()
  }

  const handleCancelCriticalChange = () => {
    setConfirmDialogOpen(false)
    resetAdvanceStep()
  }

  useEffect(() => {
    if (!wantsToAdvanceStep || isShow) return

    const handleAdvance = async () => {
      if (isEdit && isInternationalOnlyChange()) {
        snackbar.open({
          message:
            'Para alterar o tipo do documento (Nacional/Internacional), altere também o número do documento.',
          variant: 'error',
        })
        resetAdvanceStep()
        return
      }

      if (isEdit && hasCriticalChange()) {
        const validationPassed = await triggerValidation()

        if (!validationPassed) {
          resetAdvanceStep()
          return
        }

        setConfirmDialogOpen(true)
        return
      }

      triggerSubmit()
    }

    handleAdvance()
  }, [wantsToAdvanceStep])

  return (
    <>
      <Card
        id={
          isPreAvaliation
            ? STEPS.PRE_AVALIATION.CREATION.GENERAL_INFO[1]
            : STEPS.CREATION.GENERAL_INFO[1]
        }
        title="Dados Cadastrais"
      >
        <Grid container spacing={3}>
          {showSimplifiedRegisterSwitch && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Controller
                    as={<Switch color="primary" />}
                    name="simplifiedRegister"
                    control={control}
                  />
                }
                label="Cadastro Simplificado"
                labelPlacement="end"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Controller
                  as={<Switch color="primary" />}
                  name="international"
                  control={control}
                />
              }
              label="Documento Internacional"
              labelPlacement="end"
              disabled={isShow}
            />
          </Grid>

          {simplifiedRegister && !companySupplier?.supplier?.name && (
            <Grid item xs={12}>
              <Alert severity="info">
                Para cadastrar responsáveis na etapa seguinte, é necessário
                fornecer uma razão social.
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <Controller
              as={
                <TextField
                  fullWidth
                  label={`Razão Social ${simplifiedRegister ? '' : '*'}`}
                  helperText={errors?.name?.message}
                  error={!!errors?.name}
                  disabled={isShow}
                />
              }
              control={control}
              name="name"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              as={
                <TextField
                  fullWidth
                  label={international ? 'Documento *' : 'CNPJ/CPF *'}
                  helperText={errors?.document?.message}
                  error={!!errors?.document}
                  disabled={isShow}
                />
              }
              onChange={([event]) => {
                if (international) return event

                return helpers.formatters.document.identify(
                  event.target.value,
                  true,
                )
              }}
              control={control}
              name="document"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <AutocompleteTypeAndSearch
              textField={{
                label: 'Segmento',
                variant: 'outlined',
                error: !!errors?.segmentId,
                helperText: errors?.segmentId?.message,
                disabled: isShow,
              }}
              requestToLoadOptions={fetchSegments}
              name="segmentId"
              disabled={isShow}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              as={
                <TextField
                  fullWidth
                  label="Descrição"
                  multiline
                  minRows={4}
                  helperText={errors?.description?.message}
                  error={!!errors?.description}
                  disabled={isShow}
                />
              }
              control={control}
              name="description"
            />
          </Grid>
        </Grid>
      </Card>
      <ConfirmationDialog
        open={confirmDialogOpen}
        setOpen={(value) => {
          if (!value) handleCancelCriticalChange()
        }}
        actionAcceptButton={handleConfirmCriticalChange}
        textButton="Prosseguir"
        textButtonCancel="Cancelar"
        disableBackdropClick
        text="Ao alterar o documento ou o tipo de documento, todos os dados já pré-cadastrados, como auditorias e contrato, serão mantidos. Deseja prosseguir?"
      />
    </>
  )
}

export default GeneralInfoStep
